import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const NotifContext = createContext(null);

export function NotifProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const timerRef = useRef(null);

  const fetchNow = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { /* silent */ }
  }, [user]);

  useEffect(() => {
    if (!user || user === false) {
      // Defer reset to next tick so we don't call setState during parent render
      const id = setTimeout(() => { setItems([]); setUnread(0); }, 0);
      return () => clearTimeout(id);
    }
    // Defer the first fetch to avoid setState-during-render warnings when
    // AuthContext finishes hydrating on mount
    const id = setTimeout(fetchNow, 0);
    // iter 96 — poll faster (20s) so broadcasts/notifications show up promptly
    // in the bell instead of feeling like "nothing happened" for up to a minute.
    timerRef.current = setInterval(fetchNow, 20_000);
    return () => { clearTimeout(id); clearInterval(timerRef.current); };
  }, [user, fetchNow]);

  const markAllRead = useCallback(async () => {
    try {
      await api.post("/notifications/mark-read", { all: true });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) { /* silent */ }
  }, []);

  const markOneRead = useCallback(async (id) => {
    try {
      await api.post("/notifications/mark-read", { ids: [id] });
      setItems((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { /* silent */ }
  }, []);

  return (
    <NotifContext.Provider value={{ items, unread, fetchNow, markAllRead, markOneRead }}>
      {children}
    </NotifContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotifContext);
  if (!ctx) throw new Error("useNotifications must be used inside NotifProvider");
  return ctx;
}
